import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBuildingStore, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useBranches, useDeleteBranch } from './branchesApi';
import { BranchFormDrawer } from './BranchFormDrawer';
import type { BranchDto } from './types';

export function BranchesPage() {
  const { t } = useTranslation();
  const { data: branches, isLoading } = useBranches();
  const deleteMutation = useDeleteBranch();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<BranchDto | null>(null);
  const [deleting, setDeleting] = useState<BranchDto | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (branch: BranchDto) => {
    setEditing(branch);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.branchId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const hasBranches = (branches?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.organization')}
        title={t('branches.page.title')}
        subtitle={t('branches.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('branches.page.create')}
          </Button>
        }
      />

      <Paper p={hasBranches ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasBranches ? (
          <EmptyState
            icon={<IconBuildingStore size={22} stroke={1.6} />}
            title={t('branches.empty.title')}
            description={t('branches.empty.body')}
            actionLabel={t('branches.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('branches.columns.code')}</Table.Th>
                <Table.Th>{t('branches.columns.nameAr')}</Table.Th>
                <Table.Th>{t('branches.columns.nameEn')}</Table.Th>
                <Table.Th>{t('branches.columns.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {branches!.map((b) => (
                <Table.Tr key={b.branchId}>
                  <Table.Td>
                    <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                      {b.code}
                    </span>
                  </Table.Td>
                  <Table.Td fw={500}>{b.nameAr}</Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{b.nameEn ?? '—'}</Table.Td>
                  <Table.Td>
                    {b.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(b)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(b)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <BranchFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        branch={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('branches.delete.title')}
        message={t('branches.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}
